import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, RefreshCcw, Wand2, Loader2 } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { confirmDialog } from "../lib/confirm";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDate, deDateShort } from "../lib/dateFormat";

function StatusPhoto({ fileId, size = "sm" }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    (async () => {
      try {
        const res = await api.get(`/files/${fileId}`, { responseType: "blob" });
        url = URL.createObjectURL(res.data);
        setSrc(url);
      } catch (e) { console.debug("status image load failed:", e); }
    })();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [fileId]);
  const dim = size === "lg" ? "w-full aspect-[4/3]" : "w-24 h-24";
  if (!src) return <div className={`${dim} bg-navy/5 animate-pulse`} />;
  return <img src={src} alt="Status" className={`${dim} object-cover border border-navy/10`} />;
}

function mondayOf(dateStr) {
  const d = dateStr ? new Date(dateStr + "T00:00:00") : new Date();
  const day = (d.getDay() + 6) % 7; // Mon=0
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
}

export default function TeamUpdates() {
  const { t, lang } = useI18n();
  const [customers, setCustomers] = useState([]);
  const [allProperties, setAllProperties] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [form, setForm] = useState({
    customer_id: "",
    property_id: "",
    week_of: mondayOf(),
    summary: "",
    photo_file_ids: [],
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const [c, p, u, d] = await Promise.all([
      api.get("/team/customers"),
      api.get("/properties"),
      api.get("/weekly-updates"),
      api.get("/team/weekly-drafts"),
    ]);
    setCustomers(c.data);
    setAllProperties(p.data);
    setUpdates(u.data);
    setDrafts(d.data);
    const cust = c.data[0];
    if (cust) {
      const firstProp = p.data.find((pr) => pr.owner_id === cust.id);
      setForm((f) => (f.customer_id ? f : { ...f, customer_id: cust.id, property_id: firstProp?.id || "" }));
    }
  }, []);

  const [drafts, setDrafts] = useState([]);
  const [draftEdits, setDraftEdits] = useState({}); // { [id]: {summary, team_note} }
  const [busyId, setBusyId] = useState(null);

  const setDraftField = (id, field, value) => {
    setDraftEdits((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const generateNow = async () => {
    try {
      setBusyId("__gen__");
      const { data } = await api.post("/team/weekly-updates/generate-drafts");
      toast.success(`Entwürfe: ${data.drafts_created} neu · ${data.drafts_updated} aktualisiert`);
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusyId(null); }
  };

  const publishDraft = async (draft) => {
    try {
      setBusyId(draft.id);
      const edits = draftEdits[draft.id] || {};
      if (edits.summary != null || edits.team_note != null || edits.photo_file_ids != null) {
        await api.patch(`/team/weekly-updates/${draft.id}`, edits);
      }
      await api.post(`/team/weekly-updates/${draft.id}/publish`);
      toast.success("Wochenbericht freigegeben — Kunde sieht ihn jetzt in der App.");
      setDraftEdits((prev) => {
        const next = { ...prev };
        delete next[draft.id];
        return next;
      });
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusyId(null); }
  };

  const uploadDraftPhotos = async (draft, e) => {
    const files = Array.from(e.target.files || []);
    if (e.target) e.target.value = "";
    if (files.length === 0) return;
    try {
      setBusyId(draft.id);
      const current = (draftEdits[draft.id]?.photo_file_ids) || draft.photo_file_ids || [];
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.id);
      }
      setDraftField(draft.id, "photo_file_ids", [...current, ...uploaded]);
      toast.success(`${uploaded.length} Foto${uploaded.length > 1 ? "s" : ""} hochgeladen`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusyId(null); }
  };

  // Feb 2026 · Claude Sonnet 5 · Draft mit KI-Fließtext neu formulieren.
  // Der Concierge klickt „Mit Claude verfeinern", Claude bekommt die
  // Aktivitäten-Liste + Kunden- und Objekt-Kontext und liefert ein
  // sauberes Fließtext-Summary + team_note zurück. Der Concierge kann
  // danach feinschleifen und freigeben.
  const aiDraft = async (draft) => {
    try {
      setBusyId(`ai-${draft.id}`);
      const { data } = await api.post(`/team/weekly-updates/${draft.id}/ai-draft`);
      // Bestehende lokale Edits wegnehmen, damit die neue Claude-Version
      // wirklich sichtbar wird.
      setDraftEdits((prev) => {
        const next = { ...prev };
        delete next[draft.id];
        return next;
      });
      // Draft-Zeile im State updaten.
      setDrafts((prev) => prev.map((d) => (d.id === draft.id ? { ...d, ...data } : d)));
      toast.success("Claude hat den Entwurf neu formuliert.");
    } catch (err) {
      const status = err?.response?.status;
      const msg = status === 503
        ? "Claude-Assistenz gerade nicht erreichbar."
        : formatErr(err.response?.data?.detail) || err.message;
      toast.error(msg);
    } finally { setBusyId(null); }
  };

  useEffect(() => { load(); }, [load]);

  const propertiesForCustomer = allProperties.filter((p) => p.owner_id === form.customer_id);

  const customerLabel = (id) => {
    const c = customers.find((cc) => cc.id === id);
    return c?.name || c?.email || "—";
  };
  const propertyLabel = (id) => allProperties.find((p) => p.id === id)?.name || "—";

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.id);
      }
      setForm((f) => ({ ...f, photo_file_ids: [...f.photo_file_ids, ...uploaded] }));
      toast.success(`${uploaded.length} Foto${uploaded.length > 1 ? "s" : ""} hochgeladen.`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = (id) => {
    setForm((f) => ({ ...f, photo_file_ids: f.photo_file_ids.filter((x) => x !== id) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.property_id || !form.summary.trim()) {
      toast.error("Kunde, Objekt und Zusammenfassung sind erforderlich.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/weekly-updates", form);
      toast.success(t.updates.sent);
      setForm({ ...form, summary: "", photo_file_ids: [] });
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!(await confirmDialog({
      title: "Bericht löschen?",
      description: "Der Wochenbericht wird unwiderruflich entfernt.",
      confirmText: "Löschen",
      variant: "danger",
    }))) return;
    await api.delete(`/weekly-updates/${id}`);
    await load();
  };

  const photosCount = (u) => {
    if (u.photo_file_ids && u.photo_file_ids.length > 0) return u.photo_file_ids.length;
    if (u.photo_file_id) return 1;
    return 0;
  };
  const firstPhoto = (u) => {
    if (u.photo_file_ids && u.photo_file_ids.length > 0) return u.photo_file_ids[0];
    return u.photo_file_id || null;
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="team-updates">
      <div className="mb-10 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-caps mb-3">Weissblom Team</div>
          <h1 className="font-display text-4xl md:text-5xl text-navy">{t.updates.admin_title}</h1>
          <p className="text-[15px] text-navy-dark/70 mt-3 max-w-2xl">{t.updates.admin_lead}</p>
        </div>
        <button
          type="button"
          onClick={generateNow}
          disabled={busyId === "__gen__"}
          data-testid="generate-drafts-btn"
          className="inline-flex items-center gap-2 border border-navy/25 hover:border-gold text-navy px-4 py-2.5 text-[11.5px] tracking-[0.15em] uppercase transition-colors disabled:opacity-50"
        >
          <RefreshCcw className="w-3.5 h-3.5" strokeWidth={1.6} />
          {busyId === "__gen__" ? "Sammle …" : "Entwürfe jetzt sammeln"}
        </button>
      </div>

      {/* --- Auto-Draft Review --- */}
      {drafts.length > 0 && (
        <section className="mb-10 border border-gold/40" data-testid="weekly-drafts-section">
          <div className="px-5 md:px-6 py-4 border-b border-gold/30 flex items-center gap-3" style={{ background: withAlpha(BRAND_COLORS.gold, 0.08) }}>
            <Sparkles className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
            <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>
              {drafts.length} Entwurf{drafts.length === 1 ? "" : "e"} zur Freigabe
            </div>
            <div className="text-[12px] text-navy-dark/60 italic ml-2">
              Automatisch generiert · Sonntag 18:00 UTC
            </div>
          </div>
          <div className="p-5 md:p-6 space-y-6">
            {drafts.map((d) => {
              const edits = draftEdits[d.id] || {};
              const summary = edits.summary != null ? edits.summary : d.summary;
              const teamNote = edits.team_note != null ? edits.team_note : (d.team_note || "");
              const photos = edits.photo_file_ids != null ? edits.photo_file_ids : (d.photo_file_ids || []);
              return (
                <div key={d.id} className="bg-white border border-navy/10 p-5" data-testid={`weekly-draft-${d.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                    <div>
                      <div className="label-caps text-navy-dark/60 mb-1">
                        Woche vom {deDate(d.week_of)}
                        {d.week_end ? ` bis ${deDate(d.week_end)}` : ""}
                      </div>
                      <div className="font-display text-xl text-navy">
                        {d.property_name || "—"}
                        <span className="text-navy-dark/60 text-base"> · {d.customer_name || "—"}</span>
                      </div>
                    </div>
                    <div className="text-[11px] tracking-widest uppercase px-2.5 py-1" style={{ background: withAlpha(BRAND_COLORS.gold, 0.15), color: "#8B7530" }}>
                      Entwurf · {d.activities?.length || 0} Aktivität{(d.activities?.length || 0) === 1 ? "" : "en"}
                    </div>
                  </div>

                  {d.activities?.length > 0 && (
                    <ul className="mb-4 text-[12.5px] text-navy-dark/75 space-y-1 border-l-2 border-gold/50 pl-4">
                      {d.activities.slice(0, 8).map((a, i) => (
                        <li key={`${a.at || i}-${a.title}`}>
                          <span className="text-navy">{a.title}</span>
                          {a.at && <span className="text-navy-dark/50"> · {deDateShort(a.at)}</span>}
                        </li>
                      ))}
                      {d.activities.length > 8 && (
                        <li className="text-navy-dark/50 italic">+ {d.activities.length - 8} weitere</li>
                      )}
                    </ul>
                  )}

                  <label className="label-caps block mb-2">Zusammenfassung</label>
                  <textarea
                    rows={4}
                    value={summary}
                    data-testid={`draft-summary-${d.id}`}
                    onChange={(e) => setDraftField(d.id, "summary", e.target.value)}
                    className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2.5 text-[13px] text-navy-dark leading-relaxed mb-4"
                  />

                  <label className="label-caps block mb-2">Persönliches Fazit <span className="lowercase text-navy-dark/50 italic">(optional)</span></label>
                  <textarea
                    rows={2}
                    value={teamNote}
                    data-testid={`draft-note-${d.id}`}
                    placeholder="z. B. Alles ruhig, Wetter freundlich, nächste Woche steht der Gärtner an …"
                    onChange={(e) => setDraftField(d.id, "team_note", e.target.value)}
                    className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2.5 text-[13px] text-navy-dark leading-relaxed mb-4"
                  />

                  <div className="flex items-center gap-3 flex-wrap mb-4">
                    <label className="border border-navy/20 text-navy px-3 py-2 text-[11px] tracking-wider uppercase hover:border-gold transition-colors cursor-pointer">
                      Fotos hinzufügen
                      <input type="file" multiple accept="image/*" onChange={(e) => uploadDraftPhotos(d, e)} className="hidden" data-testid={`draft-upload-${d.id}`} />
                    </label>
                    {photos.length > 0 && (
                      <span className="text-[12px] text-navy-dark/70">{photos.length} Foto{photos.length === 1 ? "" : "s"}</span>
                    )}
                  </div>

                  <div className="pt-4 border-t border-navy/10 flex items-center justify-between gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => aiDraft(d)}
                      disabled={busyId === `ai-${d.id}` || (d.activities?.length || 0) === 0}
                      data-testid={`draft-ai-${d.id}`}
                      title={
                        (d.activities?.length || 0) === 0
                          ? "Kein Kontext — Claude braucht mindestens eine Aktivität."
                          : "Claude Sonnet 5 formuliert den Entwurf in Fließtext neu"
                      }
                      className="inline-flex items-center gap-1.5 border px-3 py-2 text-[11px] tracking-[0.14em] uppercase transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ borderColor: withAlpha(BRAND_COLORS.gold, 0.55), color: BRAND_COLORS.goldDark }}
                    >
                      {busyId === `ai-${d.id}` ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2} />
                          Claude denkt …
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-3 h-3" strokeWidth={2} />
                          {d.ai_generated_by ? "Neu formulieren" : "Mit Claude verfeinern"}
                        </>
                      )}
                    </button>
                    {d.ai_generated_by && (
                      <span
                        className="text-[10.5px] tracking-[0.14em] uppercase px-2 py-0.5 rounded-full"
                        style={{ background: withAlpha(BRAND_COLORS.gold, 0.14), color: BRAND_COLORS.goldDark }}
                        data-testid={`draft-ai-badge-${d.id}`}
                      >
                        Claude · {d.ai_generated_at ? deDate(d.ai_generated_at) : ""}
                      </span>
                    )}
                    <div className="flex items-center gap-3 ml-auto">
                    <button
                      type="button"
                      onClick={() => remove(d.id)}
                      className="text-[11px] tracking-widest uppercase text-navy-dark/60 hover:text-red-800"
                      data-testid={`draft-discard-${d.id}`}
                    >
                      Verwerfen
                    </button>
                    <button
                      type="button"
                      onClick={() => publishDraft(d)}
                      disabled={busyId === d.id || !summary.trim()}
                      data-testid={`draft-publish-${d.id}`}
                      className="inline-flex items-center gap-2 bg-navy text-ivory px-5 py-2.5 text-[11px] tracking-[0.16em] uppercase hover:opacity-90 transition-opacity disabled:opacity-40"
                      style={{ background: BRAND_COLORS.navy }}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} />
                      {busyId === d.id ? "…" : "Freigeben"}
                    </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <form onSubmit={submit} className="bg-white border border-navy/10 p-6 md:p-8 space-y-5" data-testid="team-update-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label-caps block mb-2">{t.updates.customer_label}</label>
              <select
                data-testid="update-customer-select"
                value={form.customer_id}
                onChange={(e) => {
                  const cid = e.target.value;
                  const firstProp = allProperties.find((p) => p.owner_id === cid);
                  setForm({ ...form, customer_id: cid, property_id: firstProp?.id || "" });
                }}
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3"
              >
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.email}</option>)}
              </select>
            </div>
            <div>
              <label className="label-caps block mb-2">{t.updates.property_label}</label>
              <select
                data-testid="update-property-select"
                value={form.property_id}
                onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3"
              >
                {propertiesForCustomer.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                {propertiesForCustomer.length === 0 && <option value="">—</option>}
              </select>
            </div>
          </div>
          <div>
            <label className="label-caps block mb-2">{t.updates.week_label}</label>
            <input
              data-testid="update-week-input"
              type="date"
              value={form.week_of}
              onChange={(e) => setForm({ ...form, week_of: mondayOf(e.target.value) })}
              className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3"
            />
          </div>
          <div>
            <label className="label-caps block mb-2">{t.updates.summary_label}</label>
            <textarea
              data-testid="update-summary-input"
              required rows={6}
              value={form.summary}
              onChange={(e) => setForm({ ...form, summary: e.target.value })}
              className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark leading-relaxed"
              placeholder="Objekt in Ordnung, Rasen gemäht, Post entgegengenommen…"
            />
          </div>
          <div>
            <label className="label-caps block mb-2">{t.updates.photos}</label>
            <div className="flex flex-wrap items-start gap-3">
              {form.photo_file_ids.map((fid) => (
                <div key={fid} className="relative group" data-testid={`photo-preview-${fid}`}>
                  <StatusPhoto fileId={fid} />
                  <button
                    type="button"
                    onClick={() => removePhoto(fid)}
                    data-testid={`remove-photo-${fid}`}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-navy text-ivory text-[14px] leading-none flex items-center justify-center border border-gold opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: BRAND_COLORS.navy }}
                    aria-label="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
              <input ref={fileRef} type="file" accept="image/*" multiple onChange={upload} className="hidden" />
              <button
                type="button"
                data-testid="update-upload-btn"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="border border-navy/20 text-navy px-4 py-2.5 text-[12px] tracking-wider uppercase hover:border-gold transition-colors disabled:opacity-50 h-24"
              >
                {uploading ? "…" : (form.photo_file_ids.length > 0 ? t.updates.add_more : t.updates.upload)}
              </button>
            </div>
          </div>
          <button
            data-testid="update-submit-btn"
            type="submit"
            disabled={submitting}
            className="bg-navy text-ivory px-8 py-3.5 text-[12px] tracking-[0.15em] uppercase hover:bg-navy-dark transition-colors disabled:opacity-50"
            style={{ background: BRAND_COLORS.navy }}
          >
            {submitting ? t.common.loading : t.updates.submit}
          </button>
        </form>

        <div className="space-y-4" data-testid="team-updates-list">
          <div className="label-caps mb-2">Zuletzt versendet</div>
          {updates.length === 0 && (
            <div className="bg-white border border-dashed border-navy/20 py-10 text-center text-[13px] text-navy-dark/50">
              {t.updates.empty}
            </div>
          )}
          {updates.map((u) => (
            <div key={u.id} className="bg-white border border-navy/10 p-5 flex gap-4 items-start">
              {firstPhoto(u) && (
                <div className="relative shrink-0">
                  <StatusPhoto fileId={firstPhoto(u)} />
                  {photosCount(u) > 1 && (
                    <span className="absolute bottom-1 right-1 bg-navy text-ivory text-[10px] px-1.5 py-0.5 tracking-widest" style={{ background: BRAND_COLORS.navy }}>
                      +{photosCount(u) - 1}
                    </span>
                  )}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="label-caps mb-1">
                  {deDate(u.week_of)} · {customerLabel(u.customer_id)}
                </div>
                <div className="font-display text-lg text-navy mb-1">{propertyLabel(u.property_id)}</div>
                <p className="text-[13px] text-navy-dark/70 leading-relaxed line-clamp-3">{u.summary}</p>
              </div>
              <button
                onClick={() => remove(u.id)}
                data-testid={`delete-update-${u.id}`}
                className="text-[11px] tracking-widest uppercase text-navy-dark/60 hover:text-red-800 transition-colors"
              >
                {t.updates.delete}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
